import { moreStackTitle } from "apollo/components/mobile/more-stack";
import { useAppHeaderHidden } from "apollo/components/mobile/tab-bar-context";
import { MoreStackBar } from "./more-stack-bar";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import React from "react";
import { cn } from "ui/lib/utils";
import { CaretSortIcon } from "@radix-ui/react-icons";
import Link from "next/link";
import { isDev } from "ui/lib/helpers";
import { useHomeHref, useLastTeam, useShellTeamId } from "apollo/lib/last-team";
import { sectionSegment, useSectionLinks } from "./links";
import { VersionTracker } from "./version-tracker";

// The desktop frames paint from the shell's cached team until the client-only
// parts load, the way the phone header does.
const TeamNameSkeleton = () => (
  <span className="inline-block h-4 w-32 rounded bg-white/[0.06] align-middle motion-safe:animate-pulse">
    <span className="sr-only">Loading team</span>
  </span>
);

const TeamsFrame = () => {
  const teamId = useShellTeamId();
  const lastTeam = useLastTeam();
  const name = lastTeam && lastTeam.id === teamId ? lastTeam.name : null;
  return (
    <span className="inline-flex items-center gap-1.5 py-1 rounded-lg text-base px-2 -mx-2">
      <span className="font-medium text-white/90 truncate max-w-[200px]">
        {name || <TeamNameSkeleton />}
      </span>
      {isDev ? <span className="text-[10px] text-white/30 font-mono">dev</span> : null}
      <CaretSortIcon className="h-4 w-4 text-white/30" aria-hidden="true" />
    </span>
  );
};

const NavigationFrame = () => {
  const teamId = useShellTeamId();
  const links = useSectionLinks();
  const { pathname } = useRouter();
  const split = pathname.split("/");
  return (
    <div className="flex items-center gap-1">
      {(teamId ? links : []).map((item) => (
        <Link
          key={item.href}
          href={`/programs/[bootcampId]/${item.href}`}
          as={`/programs/${teamId}/${item.href}`}
          className={cn(
            "relative px-3 py-1.5 text-sm rounded-lg",
            split.includes(sectionSegment(item.href))
              ? "text-white bg-white/[0.08]"
              : "text-white/40"
          )}
        >
          {item.name}
        </Link>
      ))}
      <div className="w-px h-4 bg-white/[0.08] mx-1" />
    </div>
  );
};

const Navigation = dynamic(
  () => import("./navigation").then((mod) => ({
    default: mod.Navigation
  })),
  {
    ssr: false,
    loading: () => <NavigationFrame />,
  }
);
const TeamsDropdown = dynamic(
  () => import("./teams-dropdown").then((mod) => ({
    default: mod.TeamsDropdown
  })),
  {
    ssr: false,
    loading: () => <TeamsFrame />,
  }
);
const UserDropdown = dynamic(
  () => import("./user-dropdown").then((mod) => ({
    default: mod.UserDropdown
  })),
  {
    ssr: false,
    loading: () => <span aria-hidden="true" className="flex h-8 w-8 rounded-full bg-secondary" />,
  }
);

// The server paints this until the client-only bar loads, so the header is
// there from the first frame.
const MobileHeaderFrame = () => (
  <div className="flex items-center gap-2 h-12 px-3 bg-background border-b border-white/[0.06]">
    <span className="flex items-center justify-center h-11 w-11 -ml-2 flex-shrink-0">
      <img className="w-auto h-5" src="/assets/logo/white-icon.svg" alt="" />
    </span>
    <span className="h-4 w-32 rounded bg-white/[0.06] motion-safe:animate-pulse" />
  </div>
);

const MobileHeaderBar = dynamic(
  () => import("./mobile-header").then((mod) => ({
    default: mod.MobileHeader
  })),
  {
    ssr: false,
    loading: () => <MobileHeaderFrame />,
  }
);

// The bar's height is reserved on the server so pages that measure
// #top-navigation on mount see it before the client-only bar loads.
export const MobileHeader = () => {
  const hidden = useAppHeaderHidden();
  const { pathname } = useRouter();
  const moreTitle = moreStackTitle(pathname);
  return (
    <div className={cn("h-12 lg:hidden", hidden && "hidden")}>
      {moreTitle ? <MoreStackBar title={moreTitle} /> : <MobileHeaderBar />}
    </div>
  );
};

const Logo = React.memo(() => {
  return (
    <img
      className="hidden w-auto h-5 md:block"
      src={`/assets/logo/white-icon.svg`}
      alt="Apollo Logo"
    />
  );
});

export const Header = React.memo(() => {
  const { pathname, asPath, push, query } = useRouter();

  const home = useHomeHref();
  const handleLogoClick = React.useCallback(() => {
    push(home);
  }, [push, home]);

  return (
    <nav
      key={query?.bootcampId}
      className="hidden bg-background border-b border-white/[0.06] lg:block"
    >
      <div className="hidden lg:block w-full">
        <div className="w-full px-4 py-2">
          <div className="flex items-center justify-center relative w-full min-h-[40px]">
            <div className="flex items-center absolute left-0 gap-2">
              <button
                aria-label="Home"
                onClick={handleLogoClick}
                className="rounded flex items-center flex-shrink-0"
              >
                <Logo />
              </button>
              <div aria-hidden="true" className="text-white/10 text-lg font-light select-none">/</div>
              <TeamsDropdown />
            </div>
            <Navigation />
            <div className="absolute right-0 flex items-center">
              <VersionTracker>
                <UserDropdown />
              </VersionTracker>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
});
